import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import TerminalProtectedRoute from './components/TerminalProtectedRoute';
import AuthRedirect from './components/AuthRedirect';
import NewsletterPopup from './components/NewsLetterPopup';
import CookieConsentBanner from './components/CookieConsentBanner';
import TenantTracking from './components/TenantTracking';
import PWAManifestManager from './components/PWAManifestManager';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import MaintenanceBanner from './components/MaintenanceBanner';
import UpdateBanner from './components/UpdateBanner';
import axios from 'axios';
import './utils/axiosDefaults'; // Global CSRF + credentials for all axios calls
import { useUserData } from './auth/UserData';
import { CircularProgress, Box } from '@mui/material';
import { RolePreviewProvider } from './context/RolePreviewContext';
import RolePreviewBanner from './components/RolePreviewBanner';
import { PopoutProvider } from './contexts/PopoutContext';
import { useTheme as useTenantTheme } from './context/ThemeContext';
import TerminalAuthShell, { useIsMobile } from './pages/terminal/TerminalAuthShell';

// ── Landing / public pages (small, loads fast) ──
import Home from './pages/Home';
import Docs from './pages/Docs';
import WhitelabelSales from './pages/WhitelabelSales';
import Login from './pages/LogIn';
import SignUp from './pages/SignUp';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Membership from './pages/Membership';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Unsubscribe from './pages/Unsubscribe';
import VerifyEmail from './pages/VerifyEmail';

// ── Lazy-loaded chunks ──

// Chunk: Public features (sentiment, calendar, signals, community, news)
const Sentiment = React.lazy(() => import('./pages/Sentiment'));
const SentimentAssetPage = React.lazy(() => import('./pages/SentimentAssetPage'));
const ForexNewsCalendar = React.lazy(() => import('./pages/Calendar'));
const SignalStats = React.lazy(() => import('./pages/Signals/SignalDashboard'));
const NewsDeskPage = React.lazy(() => import('./pages/Newsdesk/NewsDeskPage'));
const CommunityDirectory = React.lazy(() => import('./pages/Community/Directory'));
const TraderProfilePage = React.lazy(() => import('./pages/Community/TraderProfilePage'));
const CompetitionsPage = React.lazy(() => import('./pages/CompetitionsPage'));
const TradingRooms = React.lazy(() => import('./pages/TradingRooms'));
const MentorApply = React.lazy(() => import('./pages/MentorApply'));
// Dashboard removed — all features moved to /app/*

// Chunk: App (logged-in user shell)
const AppShell = React.lazy(() => import('./pages/app/AppShell'));

// Chunk: Standalone marketing landing (own nav + footer, no AppLayout chrome)
const LandingV2 = React.lazy(() => import('./pages/landing/LandingV2'));
const LandingAbout = React.lazy(() => import('./pages/landing/pages/About'));
const LandingContact = React.lazy(() => import('./pages/landing/pages/Contact'));
const LandingAffiliates = React.lazy(() => import('./pages/landing/pages/Affiliates'));
const LandingChangelog = React.lazy(() => import('./pages/landing/pages/Changelog'));
const LandingTerms = React.lazy(() => import('./pages/landing/pages/Terms'));
const LandingPrivacy = React.lazy(() => import('./pages/landing/pages/Privacy'));
const LandingRisk = React.lazy(() => import('./pages/landing/pages/RiskDisclosure'));
const LandingSecurity = React.lazy(() => import('./pages/landing/pages/Security'));
const LandingStreamOverlay = React.lazy(() => import('./pages/landing/pages/StreamOverlay'));

// Chunk: Standalone terminal (fullscreen, no AppShell)
const TerminalStandalone = React.lazy(() => import('./pages/terminal/TerminalStandalone'));
const TerminalLogin = React.lazy(() => import('./pages/terminal/TerminalLogin'));
const TerminalForgotPassword = React.lazy(() => import('./pages/terminal/TerminalForgotPassword'));
const TerminalResetPassword = React.lazy(() => import('./pages/terminal/TerminalResetPassword'));
const TerminalPanelPopout = React.lazy(() => import('./pages/terminal/TerminalPanelPopout'));
const TerminalWorkspacePopout = React.lazy(() => import('./pages/terminal/TerminalWorkspacePopout'));

// Chunk: Academy
const AcademyHome = React.lazy(() => import('./pages/Academy/AcademyHome'));
const CourseList = React.lazy(() => import('./pages/Academy/CourseList'));
const CourseDetail = React.lazy(() => import('./pages/Academy/CourseDetails'));
const MentorCourseList = React.lazy(() => import('./pages/Academy/MentorCourseList'));
const MentorCourseDetail = React.lazy(() => import('./pages/Academy/MentorCourseDetail'));
const MentorProfilePage = React.lazy(() => import('./pages/Academy/MentorProfilePage'));
const QuizDetails = React.lazy(() => import('./pages/Academy/QuizDetails'));
const MentorshipHub = React.lazy(() => import('./pages/Academy/MentorshipHub'));
const MentorshipPostDetail = React.lazy(() => import('./pages/Academy/MentorshipPostDetail'));

// Chunk: Trade Journal
const TradeJournalLayout = React.lazy(() => import('./components/TradeJournal/TradeJournalLayout'));
const TradeJournalDashboard = React.lazy(() => import('./components/TradeJournal/TradeJournalDashboard'));
const TradeJournalPage = React.lazy(() => import('./components/TradeJournal/TradeJournalPage'));
const ImportPage = React.lazy(() => import('./components/TradeJournal/ImportPage'));
const TradeJournalAccountPage = React.lazy(() => import('./components/TradeJournal/TradeJournalAccountPage'));

// Chunk: Admin (only loaded for admin users)
const AdminLayout = React.lazy(() => import('./pages/Admin/AdminLayout'));
const AdminDashboard = React.lazy(() => import('./pages/Admin/Dashboard'));
const AdminAccounts = React.lazy(() => import('./pages/Admin/Accounts'));
const AdminUsers = React.lazy(() => import('./pages/Admin/Users'));
const AdminUserDetail = React.lazy(() => import('./pages/Admin/UserDetail'));
const AdminSettings = React.lazy(() => import('./pages/Admin/Settings'));
const AdminMailbox = React.lazy(() => import('./pages/Admin/MailBox'));
const AdminAuditLog = React.lazy(() => import('./pages/Admin/AuditLog'));
const AdminSystemHealth = React.lazy(() => import('./pages/Admin/SystemHealth'));
const AdminFeatureFlags = React.lazy(() => import('./pages/Admin/FeatureFlags'));
const AdminTerminalAccess = React.lazy(() => import('./pages/Admin/TerminalAccess'));
const AdminJournalSettings = React.lazy(() => import('./pages/Admin/JournalSettings'));
const AdminJournalTrades = React.lazy(() => import('./pages/Admin/JournalTrades'));
const AdminJournalLanding = React.lazy(() => import('./pages/Admin/JournalLanding'));
const AdminRoles = React.lazy(() => import('./pages/Admin/Roles'));
const AdminTenants = React.lazy(() => import('./pages/Admin/Tenants'));
const AdminIpRules = React.lazy(() => import('./pages/Admin/IpRules'));
const AdminModeration = React.lazy(() => import('./pages/Admin/Moderation'));
const AdminMemberships = React.lazy(() => import('./pages/Admin/MembershipAdmin'));
const AdminMembershipPlans = React.lazy(() => import('./pages/Admin/MembershipPlans'));
const AdminStripeCoupons = React.lazy(() => import('./pages/Admin/StripeCoupons'));
const AdminEngagement = React.lazy(() => import('./pages/Admin/EngagementAnalytics'));
const AdminMentorApplications = React.lazy(() => import('./pages/Admin/MentorApplications'));
const AdminVerifications = React.lazy(() => import('./pages/Admin/Verifications'));
const AdminAffiliates = React.lazy(() => import('./pages/Admin/Affiliates'));
const AdminIndicators = React.lazy(() => import('./pages/Admin/Indicators'));
const AdminCompetitions = React.lazy(() => import('./pages/Admin/Competitions'));
const AdminTradingRooms = React.lazy(() => import('./pages/Admin/TradingRooms'));
const AdminCommunity = React.lazy(() => import('./pages/Admin/Community/CommunityDirectory'));
const AdminAcademy = React.lazy(() => import('./pages/Admin/Academy/Academy'));
const NewsletterDashboard = React.lazy(() => import('./pages/Admin/Newsletter/Newsletter'));
const NewsletterDetails = React.lazy(() => import('./pages/Admin/Newsletter/NewsletterDetails'));
const NewsletterSender = React.lazy(() => import('./pages/Admin/Newsletter/NewsletterCompose'));
const CrmAnalytics = React.lazy(() => import('./pages/Admin/CrmAnalytics'));
const AdminRevenueDashboard = React.lazy(() => import('./pages/Admin/RevenueDashboard'));
const AdminBotMarketplace = React.lazy(() => import('./pages/Admin/BotMarketplace'));
const AdminBotMonitor = React.lazy(() => import('./pages/Admin/BotMonitor'));
const AdminTvSignals = React.lazy(() => import('./pages/Admin/TvSignals'));
const AdminEmailTemplates = React.lazy(() => import('./pages/Admin/EmailTemplates'));
const AdminOnboardingEmails = React.lazy(() => import('./pages/Admin/OnboardingEmails'));
const AdminMarketIntelligence = React.lazy(() => import('./pages/Admin/MarketIntelligence'));
const AdminBugReports = React.lazy(() => import('./pages/Admin/BugReports'));

// ── Loading fallback ──
const LazyFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress />
  </Box>
);

// Terminal-themed fullscreen fallback — used while tenantConfig loads and
// while the TerminalStandalone chunk streams in.
const TerminalFallback = () => (
  <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#050a08' }}>
    <CircularProgress size={28} sx={{ color: '#22c55e' }} />
  </Box>
);

// Standalone /terminal route — renders TerminalStandalone fullscreen
// without AppShell. Four explicit states, checked in order:
//   1. tenantConfig not yet loaded → spinner (never redirect here; a flash
//      redirect would hit every user on cold cache / slow network)
//   2. viewport under 1024px → render TerminalAuthShell with no children
//      so the shell's mobile-block view is shown with tenant branding.
//      Checked before the feature gate so mobile users always see the
//      "Desktop required" message, not a dashboard-notice redirect they
//      can't act on.
//   3. loaded, feature absent → <Navigate replace> to dashboard notice
//      (replace, so Back doesn't loop back into /terminal)
//   4. loaded, feature present → render terminal
// Unauthenticated access is handled by the outer TerminalProtectedRoute,
// which redirects to /terminal/login (not the main-site /login) so the
// user stays inside the terminal branded flow.
const TerminalRoute = () => {
  const { tenantConfig } = useTenantTheme();
  const isMobile = useIsMobile();

  if (!tenantConfig) return <TerminalFallback />;

  if (isMobile) return <TerminalAuthShell />;

  const features = Array.isArray(tenantConfig.features) ? tenantConfig.features : [];
  if (!features.includes('terminal')) {
    return <Navigate to="/app/dashboard?notice=terminal_not_available" replace />;
  }

  return (
    <Suspense fallback={<TerminalFallback />}>
      <TerminalStandalone onExit={() => window.close()} />
    </Suspense>
  );
};

const trackEvent = async (event, details = {}) => {
  try {
    await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/stats/track-visit`, {
      page: event,
      details,
    });
  } catch (error) {
    console.error('Error tracking event:', error.message);
  }
};

function AppLayout({ children }) {
  const location = useLocation();
  const isNewsDeskPage = location.pathname === '/news-desk';

  const { user } = useUserData();

  // Track page views
  useEffect(() => {
    // Track Page Views
    trackEvent('PageView', { page: location.pathname, userId: user?.internalId || 'Guest' });
    
  }, [location, user]);
  
  // Track Clicks for Links and Buttons
  useEffect(() => {
    const handleClick = (event) => {
      const { tagName, innerText } = event.target;
  
      // Only log clicks for buttons and links
      if (tagName === 'BUTTON' || tagName === 'A') {
        trackEvent('Click', {
          tag: tagName,
          text: innerText?.slice(0, 50) || '', // Limit text length for logging
          userId: user?.internalId || 'Guest', // Log userId if available
        });
      }
    };
  
    document.addEventListener('click', handleClick);
  
    // Cleanup listener
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [user]);

  return (
    <>
      <a href="#main-content" className="skip-to-content">Hopp til hovedinnhold</a>
      {!isNewsDeskPage && <Header />}
      <main id="main-content">{children}</main>
      {!isNewsDeskPage && <Footer />}
    </>
  );
}

// Hosts that serve FlockTrade itself (not a white-label tenant). Used as a
// synchronous fallback before the tenant config has loaded.
const FLOCKTRADE_HOSTS = new Set([
  'flocktrade.com', 'www.flocktrade.com', 'localhost', '127.0.0.1',
]);

/*
 * The homepage is tenant-aware:
 *   - FlockTrade        → the v2 marketing landing (own full-bleed chrome).
 *   - White-label tenant → the original Home, wrapped in AppLayout's
 *                          Header/Footer (now repurposed as the tenant /
 *                          community landing).
 * Decide from the resolved tenant slug; before config loads, fall back to the
 * hostname so neither side flashes the wrong page.
 */
function HomeRoute() {
  const { tenantConfig } = useTenantTheme();
  const slug = tenantConfig?.slug;
  const isFlockTrade = slug
    ? slug === 'flocktrade'
    : FLOCKTRADE_HOSTS.has(window.location.hostname);
  return isFlockTrade ? <LandingV2 /> : <AppLayout><Home /></AppLayout>;
}

function App() {
  return (
    <RolePreviewProvider>
      <PopoutProvider>
      <RolePreviewBanner />
      <PWAManifestManager />
      <TenantTracking />
      <MaintenanceBanner />
      <UpdateBanner />
      {/* Selvsanerende: viser seg bare på de offentlige landingsrutene.
          Se LANDING_PATHS i komponenten. */}
      <NewsletterPopup />
      <CookieConsentBanner />
      <RouteErrorBoundary>
      <Suspense fallback={<LazyFallback />}>
      <Routes>
        <Route
          path="/terminal"
          element={
            <TerminalProtectedRoute>
              <TerminalRoute />
            </TerminalProtectedRoute>
          }
        />
        <Route
          path="/terminal/panel/:key"
          element={
            <TerminalProtectedRoute>
              <TerminalPanelPopout />
            </TerminalProtectedRoute>
          }
        />
        <Route
          path="/terminal/workspace/:id"
          element={
            <TerminalProtectedRoute>
              <TerminalWorkspacePopout />
            </TerminalProtectedRoute>
          }
        />
        {/* Standalone marketing landing + sub-pages — rendered without
            AppLayout so they own the full-bleed v2 nav and footer. These take
            precedence over the AppLayout-wrapped routes below (e.g. /privacy). */}
        {/* Tenant-aware homepage: FlockTrade → v2 landing; tenant → old Home.
            Matches before the AppLayout "*" catch-all below. */}
        <Route path="/" element={<HomeRoute />} />
        <Route path="/v2" element={<LandingV2 />} />
        <Route path="/about" element={<LandingAbout />} />
        <Route path="/contact" element={<LandingContact />} />
        <Route path="/affiliates" element={<LandingAffiliates />} />
        <Route path="/changelog" element={<LandingChangelog />} />
        <Route path="/terms" element={<LandingTerms />} />
        <Route path="/privacy" element={<LandingPrivacy />} />
        <Route path="/risk-disclosure" element={<LandingRisk />} />
        <Route path="/security" element={<LandingSecurity />} />
        {/* OBS overlay generator. The overlays themselves (/stream/bar etc.) are
            server-rendered by Express, proxied by Apache — see public/.htaccess. */}
        <Route path="/stream/overlay" element={<LandingStreamOverlay />} />
        {/* Pricing is part of the marketing surface, not the app: it carries the
            v2 nav and footer like every other public page. It previously sat in
            the AppLayout branch below, which framed the current pricing cards
            with the old MUI header and footer. */}
        <Route path="/pricing" element={<Membership />} />
        {/* Legacy URL — redirect to the canonical /pricing, preserving query (e.g. Stripe ?state=success) */}
        <Route path="/membership" element={<Navigate to={`/pricing${window.location.search}`} replace />} />
        {/* Plausible guesses at the pricing URL. Without these they fall through
            to the catch-all, which renders an unstyled 404 inside the old
            chrome, so a mistyped link looks like a neglected site. */}
        <Route path="/prices" element={<Navigate to={`/pricing${window.location.search}`} replace />} />
        <Route path="/plans" element={<Navigate to={`/pricing${window.location.search}`} replace />} />
        <Route path="/price" element={<Navigate to={`/pricing${window.location.search}`} replace />} />
        {/* Auth pages — full-bleed v2 chrome, no AppLayout Header/Footer. */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/terminal/login" element={<TerminalLogin />} />
        <Route path="/terminal/forgot-password" element={<TerminalForgotPassword />} />
        <Route path="/terminal/reset-password" element={<TerminalResetPassword />} />
        <Route
          path="/app/*"
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        />
        {/* Public routes with Header and Footer */}
        <Route
          path="*"
          element={
            <AppLayout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/docs" element={<Docs />} />
                <Route path="/sentiment" element={<Sentiment />} />
                <Route path="/sentiment/:slug" element={<SentimentAssetPage />} />
                <Route path="/calendar" element={<ForexNewsCalendar />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                {/* Old /account removed — canonical is /app/account */}
                <Route path="/account" element={<Navigate to="/app/account" replace />} />
                <Route path="/signals" element={<SignalStats />} />
                <Route path="/community" element={<CommunityDirectory />} />
                <Route path="/u/:username" element={<TraderProfilePage />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                
                <Route path="/unsubscribe" element={<Unsubscribe />} />

                {/* Academy redirects — moved to /app/academy/* */}
                <Route path="/academy" element={<Navigate to="/app/academy" replace />} />
                <Route path="/academy/*" element={<Navigate to="/app/academy" replace />} />


                <Route path="/whitelabel" element={<WhitelabelSales />} />
                <Route path="/mentor-apply" element={<MentorApply />} />
                <Route path="/competitions" element={<CompetitionsPage />} />
                <Route path="/trading-rooms" element={<TradingRooms />} />
                {/* Old /dashboard removed — redirect to /app */}
                <Route path="/dashboard" element={<Navigate to="/app" replace />} />
                <Route path="/news-desk" element={<NewsDeskPage />} />
                <Route path="*" element={<AuthRedirect />} />
              </Routes>
            </AppLayout>
          }
        />

        {/* Admin routes without Header and Footer */}
        <Route
          path="/dashboard/admin/*"
          element={
            <ProtectedRoute requiredPermissions={['owner', 'admin', 'tech', 'moderator', 'mentor', 'manage_users', 'manage_settings', 'manage_technical', '*']}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="accounts" element={<AdminAccounts />} />
          <Route path="journal" element={<AdminJournalLanding />} />
          <Route path="journal/accounts" element={<AdminAccounts />} />
          <Route path="journal/trades" element={<AdminJournalTrades />} />
          <Route path="journal/settings" element={<AdminJournalSettings />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="users/:internalId" element={<AdminUserDetail />} />
          <Route path="crm-analytics" element={<CrmAnalytics />} />
          <Route path="community" element={<AdminCommunity />} />
          <Route path="mailbox" element={<AdminMailbox />} />
          <Route path="email-templates" element={<AdminEmailTemplates />} />
          <Route path="onboarding-emails" element={<AdminOnboardingEmails />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="audit-log" element={<AdminAuditLog />} />
          <Route path="system-health" element={<AdminSystemHealth />} />
          <Route path="feature-flags" element={<AdminFeatureFlags />} />
          <Route path="terminal-access" element={<AdminTerminalAccess />} />
          <Route path="roles" element={<AdminRoles />} />
          <Route path="ip-rules" element={<AdminIpRules />} />
          <Route path="moderation" element={<AdminModeration />} />
          <Route path="memberships" element={<AdminMemberships />} />
          <Route path="membership-plans" element={<AdminMembershipPlans />} />
          <Route path="stripe-coupons" element={<AdminStripeCoupons />} />
          <Route path="engagement" element={<AdminEngagement />} />
          <Route path="mentor-applications" element={<AdminMentorApplications />} />
          <Route path="verifications" element={<AdminVerifications />} />
          <Route path="affiliates" element={<AdminAffiliates />} />
          <Route path="indicators" element={<AdminIndicators />} />
          <Route path="competitions" element={<AdminCompetitions />} />
          <Route path="trading-rooms" element={<AdminTradingRooms />} />
          <Route path="tenants" element={<AdminTenants />} />
          <Route path="revenue" element={<AdminRevenueDashboard />} />
          <Route path="market-intelligence" element={<AdminMarketIntelligence />} />
          <Route path="bots" element={<AdminBotMarketplace />} />
          <Route path="bot-monitor" element={<AdminBotMonitor />} />
          <Route path="tv-signals" element={<AdminTvSignals />} />
          <Route path="newsletter" element={ <NewsletterDashboard /> } />
          <Route path="newsletter/create" element={ <NewsletterSender />} />
          <Route path="newsletter/:id" element={<NewsletterDetails />} />
          {/* The dashboard has always linked here; the route never existed, so
              every "Statistics" button landed on a blank 404. */}
          <Route path="newsletter/:id/statistics" element={<NewsletterDetails statsOnly />} />
          <Route path="academy" element={<AdminAcademy />} />
          <Route
            path="mentor-academy"
            element={
              <AdminAcademy
                apiBase="/api/mentor-academy"
                labels={{
                  pageTitle: 'Mentorinnhold',
                  addCategory: 'Ny mentor-kategori',
                }}
              />
            }
          />
          <Route path="bug-reports" element={<AdminBugReports />} />

        </Route>


      <Route path="/journal" element={<TradeJournalLayout />}>
        <Route index element={<TradeJournalDashboard />} />
        <Route path="dashboard" element={<TradeJournalDashboard />} />
        <Route path="data" element={<TradeJournalPage />} />
        <Route path="import" element={<ImportPage />} />
        <Route path="account" element={<TradeJournalAccountPage />} />
      </Route>
      </Routes>
      </Suspense>
      </RouteErrorBoundary>

      </PopoutProvider>
    </RolePreviewProvider>
  );
}

export default App;
